<script setup lang="ts">
import type { GeolocationListItemResponse, VehicleTypeInteger } from '@yescapa-dev/ysc-api-js/legacy'
import { Constants } from '@yescapa-dev/ysc-api-js/legacy'
import type { AppCamperSearch, AppCamperSearchQuery, VehicleBed, VehicleSeatBelt } from '@yescapa-dev/ysc-api-js/modern'
import { isRawApiError } from '~/utils/error/isRawApiError'
import type { LocationQueryRaw } from '#vue-router'
import { YSC_API_PLACES_ERROR, YSC_API_SEARCH_ERROR } from '~/utils/error/YscErrorClasses'

definePageMeta({
  middleware: [
    defineNuxtRouteMiddleware(async (to) => {
      const { fetchPageData } = useCampervanHire()
      try {
        await fetchPageData(paramValueToString(to.params.slug))
      }
      catch (e) {
        if (e instanceof Error) {
          if (isRawApiError(e) && e?.response?.status === 406) {
            // @ts-expect-error untyped but documented error
            return navigateTo({ name: 'campervan-hire-slug', params: { slug: e.response.data.correct_slug } }, { redirectCode: 302 })
          }
          throw createError({ statusCode: 404, cause: useNuxtApp().$errorManager({ e, name: YSC_API_PLACES_ERROR }) })
        }
      }
    }),
  ],
})

const { pageData } = storeToRefs(useCampervanHire())
const { t } = useI18n()
const route = useRoute()
const slug = computed(() => paramValueToString(route.params.slug))

const { stats } = useStatsStore()

const { $api } = useYscApi()
const { $errorManager } = useErrorManager()

const { LOWPROFILE, COACHBUILT, ACLASS, CAMPERVAN, VAN } = Constants.PRODUCTS.TYPES

interface LandingQuery {
  types?: VehicleTypeInteger | VehicleTypeInteger[]
  seatbelts?: VehicleSeatBelt
  beds?: VehicleBed
  pets?: boolean
  latitude?: number
  longitude?: number
  radius?: number
}

const lowprofilesQuery: LandingQuery = { types: LOWPROFILE }
const campervansQuery: LandingQuery = { types: CAMPERVAN }
const vansQuery: LandingQuery = { types: VAN }
const couplesQuery: LandingQuery = { seatbelts: 2, beds: 2 }
const familiesQuery: LandingQuery = { types: [LOWPROFILE, COACHBUILT, ACLASS] }
const petsQuery: LandingQuery = { ...familiesQuery, pets: true }
const CAMPERS_PER_ROW = 3

const toSearchProductsQueryParameters = ({ types, seatbelts, beds, pets, latitude, longitude, radius }: LandingQuery): AppCamperSearchQuery => {
  const searchableQuery: AppCamperSearchQuery = {
    page_size: CAMPERS_PER_ROW,
    latitude,
    longitude,
    radius,
  }

  if (types !== undefined && Array.isArray(types)) {
    searchableQuery.types = types.join(',')
  }
  else if (types !== undefined) {
    searchableQuery.types = `${types}`
  }

  if (seatbelts !== undefined) {
    searchableQuery.seatbelts = seatbelts
  }

  if (beds !== undefined) {
    searchableQuery.beds = beds
  }

  if (pets !== undefined) {
    searchableQuery.pets = pets
  }

  return searchableQuery
}

const toLocationQueryRaw = ({ types, seatbelts, beds, pets }: LandingQuery): LocationQueryRaw => {
  const linkableQuery: LocationQueryRaw = { types, seatbelts, beds }
  if (pets !== undefined) {
    linkableQuery.pets = pets ? 'true' : 'false'
  }
  return linkableQuery
}

const { searchCampers } = useSearchCampers()

// FIXME api-js
type PlaceType = 'COUNTRY' | 'LVL1' | 'LVL2' | 'LOC' | 'SUB' | 'POI'
type PlaceSlug = { slug: string, place_type: PlaceType, name: string }

const placeTypeHierarchy: PlaceType[] = ['COUNTRY', 'LVL1', 'LVL2', 'LOC', 'SUB', 'POI']

const { data: searchData, status } = useLazyAsyncData(`page-campervan-hire-${slug.value}-search`, async () => {
  const dynamicQuery = {
    latitude: pageData.value?.latitude,
    longitude: pageData.value?.longitude,
    radius: pageData.value?.radius,
  }

  const [
    countryRequest,
    lowprofilesRequest,
    campervansRequest,
    vansRequest,
    couplesRequest,
    familiesRequest,
    petsRequest,
  ] = await Promise.allSettled([
    $api.geolocation.getPlaces({ place_type: 'COUNTRY', allowed_on_home: true }),
    searchCampers(toSearchProductsQueryParameters({ ...dynamicQuery, ...lowprofilesQuery })),
    searchCampers(toSearchProductsQueryParameters({ ...dynamicQuery, ...campervansQuery })),
    searchCampers(toSearchProductsQueryParameters({ ...dynamicQuery, ...vansQuery })),
    searchCampers(toSearchProductsQueryParameters({ ...dynamicQuery, ...couplesQuery })),
    searchCampers(toSearchProductsQueryParameters({ ...dynamicQuery, ...familiesQuery })),
    searchCampers(toSearchProductsQueryParameters({ ...dynamicQuery, ...petsQuery })),
  ])

  let countries: GeolocationListItemResponse[] = [],
    lowprofiles: AppCamperSearch[] = [],
    campervans: AppCamperSearch[] = [],
    vans: AppCamperSearch[] = [],
    families: AppCamperSearch[] = [],
    couples: AppCamperSearch[] = [],
    pets: AppCamperSearch[] = []

  if (countryRequest.status === 'rejected') {
    $errorManager({ e: countryRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    countries = countryRequest.value.results
  }

  if (lowprofilesRequest.status === 'rejected') {
    $errorManager({ e: lowprofilesRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    lowprofiles = lowprofilesRequest.value.results
  }

  if (campervansRequest.status === 'rejected') {
    $errorManager({ e: campervansRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    campervans = campervansRequest.value.results
  }

  if (vansRequest.status === 'rejected') {
    $errorManager({ e: vansRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    vans = vansRequest.value.results
  }

  if (couplesRequest.status === 'rejected') {
    $errorManager({ e: couplesRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    families = couplesRequest.value.results
  }

  if (familiesRequest.status === 'rejected') {
    $errorManager({ e: familiesRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    couples = familiesRequest.value.results
  }

  if (petsRequest.status === 'rejected') {
    $errorManager({ e: petsRequest.reason, name: YSC_API_SEARCH_ERROR })
  }
  else {
    pets = petsRequest.value.results
  }

  return {
    countries,
    lowprofiles,
    campervans,
    vans,
    families,
    couples,
    pets,
  }
}, {
  watch: [slug],
})

const { isPending } = useIsPending(status)

const closestAncestor = computed(() => {
  if (!pageData.value?.ancestors) {
    return null
  }
  const [closestAncestor] = pageData.value.ancestors.slice(-1)
  return closestAncestor
})

const placeNameWithClosestAncestor = computed(() => {
  if (!pageData.value?.name) {
    return null
  }

  let name = pageData.value.name
  if (closestAncestor.value) {
    name += ` (${closestAncestor.value.name})`
  }

  return name
})

const bgImage = computed(() => {
  if (!pageData.value?.picture_id) {
    return null
  }

  return twicpicsPath(pageData.value.picture_id)
})

const { getTranslatedLink } = useGetTranslatedLink()
const crumbs = computed(() => {
  if (!pageData.value?.ancestors) {
    return null
  }

  return [
    { name: getTranslatedLink('index'), slug: 'index' },
    ...pageData.value.ancestors,
    { name: pageData.value.name, slug: route.params.slug, place_type: pageData.value.place_type },
  ]
})

const groupedNeighbors = computed(() => {
  if (!pageData.value?.neighbors) {
    return null
  }

  const groupedByPlaceType = pageData.value.neighbors.reduce<{ [key: string]: PlaceSlug[] }>((acc, cur) => {
    const key = cur.place_type === 'SUB' || cur.place_type === 'POI' ? 'LOC' : cur.place_type
    if (key in acc) {
      acc[key].push(cur)
    }
    else {
      acc[key] = [cur]
    }
    return acc
  }, {})

  const groupedAndSorted = sortArrayByProps(
    Object.entries(groupedByPlaceType).map(([place_type, list]) => {
      return {
        place_type,
        list,
      }
    }),
    'place_type',
    placeTypeHierarchy,
  )

  return groupedAndSorted.map(({ list, place_type }) => {
    const defaultTitle = pageData.value?.place_type !== 'COUNTRY' ? t('commons.and_surrounding_area_dynamic', { slug_string: pageData.value?.name }) : pageData.value?.name
    const placeIndex = placeTypeHierarchy.findIndex(x => x === place_type)
    const ancestor_type = placeTypeHierarchy[placeIndex - 1]
    const ancestor = pageData.value?.ancestors.find(({ place_type }) => place_type === ancestor_type)
    return {
      title: place_type === 'LOC' ? defaultTitle : ancestor?.name ?? defaultTitle,
      ancestor,
      list,
    }
  }).reverse()
})

const mapZoom = computed(() => {
  if (!pageData.value) {
    return 0
  }
  if (pageData.value.place_type === 'COUNTRY') {
    return 5
  }
  if (pageData.value.place_type === 'LVL1' || pageData.value.place_type === 'LVL2') {
    return 7
  }
  return 10
})

useSeoText({ title: `${t('commons.rent_a_camper.title_seo')} - ${pageData.value?.name}` })

const customPaths = computed (() => {
  if (!pageData.value) {
    return []
  }
  return pageData.value.alternate_slugs.map(({ language, slug }) => {
    const locale = language === 'pt' ? 'pt-pt' : language
    return {
      locale, customPath: `/campervan-hire/${slug}`,
    }
  })
})

useHeadAlternateLinks({ customPaths })
</script>

<template>
  <div class="overflow-hidden">
    <AppPageHero
      :bg-image="bgImage"
      :bg-image-alt="`${$t('commons.rent_a_camper.title_seo')} - ${pageData?.name}`"
      with-stamp
      with-drawing
    >
      <template #title-big>
        {{ $t('pages.index.title') }}
      </template>
      <template #title-seo>
        {{ $t('commons.rent_a_camper.title_seo') }} — {{ pageData?.name }}
      </template>
    </AppPageHero>

    <AppPageHeroBanner
      :average="stats.average"
      :count="stats.count"
    />

    <div class="container">
      <YscBreadcrumbs
        v-if="crumbs"
        :crumbs="crumbs"
      >
        <template #default="{ crumb }">
          <NuxtLink
            :to="crumb.slug === 'index' ? { name: crumb.slug } : { name: 'campervan-hire-slug', params: { slug: crumb.slug } }"
            class="focus-visible:ring-peacock-500 font-light hover:underline focus-visible:rounded focus-visible:ring-2 group-last:font-semibold"
            itemprop="item"
          >
            <span itemprop="name">
              {{ crumb.name }}
            </span>
          </NuxtLink>
        </template>
      </YscBreadcrumbs>
    </div>

    <div class="relative">
      <AppLoadingOverlay v-if="isPending" />
      <AppCamperSearchCall
        v-if="searchData && searchData.lowprofiles.length >= CAMPERS_PER_ROW"
        :campers="searchData?.lowprofiles ?? []"
        class="container mt-12"
      >
        <template #title>
          {{ $t('commons.rent_a_camper.lowprofile') }} — {{ pageData?.name }}
        </template>
        <template #description>
          {{ $t('pages.places.lowprofile_advantages') }}
        </template>
        <template #cta>
          <NuxtLink
            :to="{ name: 's', query: toLocationQueryRaw(lowprofilesQuery) }"
            class="btn btn-secondary"
          >
            {{ $t('commons.rent_a_camper.lowprofile') }}
          </NuxtLink>
        </template>
      </AppCamperSearchCall>

      <AppCamperSearchCall
        v-if="searchData && searchData.campervans.length >= CAMPERS_PER_ROW"
        :campers="searchData.campervans"
        class="container mt-20"
      >
        <template #title>
          {{ $t('commons.rent_a_camper.campervan') }} — {{ pageData?.name }}
        </template>
        <template #description>
          {{ $t('pages.places.campervan_advantages') }}
        </template>
        <template #cta>
          <NuxtLink
            :to="{ name: 's', query: toLocationQueryRaw(campervansQuery) }"
            class="btn btn-secondary"
          >
            {{ $t('commons.rent_a_camper.campervan') }}
          </NuxtLink>
        </template>
      </AppCamperSearchCall>

      <AppCamperSearchCall
        v-if="searchData && searchData.vans.length >= CAMPERS_PER_ROW"
        :campers="searchData.vans"
        class="container mt-20"
      >
        <template #title>
          {{ $t('commons.rent_a_camper.van') }} — {{ pageData?.name }}
        </template>
        <template #description>
          {{ $t('pages.places.van_advantages') }}
        </template>
        <template #cta>
          <NuxtLink
            :to="{ name: 's', query: toLocationQueryRaw(vansQuery) }"
            class="btn btn-secondary"
          >
            {{ $t('commons.rent_a_camper.van') }}
          </NuxtLink>
        </template>
      </AppCamperSearchCall>

      <div
        v-if="pageData?.description_html"
        class="mt-12 lg:relative"
      >
        <div class="hidden lg:absolute lg:w-1/2 lg:top-0 lg:right-0 lg:bottom-0 lg:flex lg:items-center">
          <YscMapStatic
            v-if="pageData.latitude && pageData.longitude"
            :lat="pageData.latitude"
            :lng="pageData.longitude"
            :zoom="mapZoom"
            class="h-full w-full"
          />
        </div>
        <div class="container lg:overflow-hidden">
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="prose max-w-full py-16 lg:w-1/2 lg:bg-white lg:relative lg:shadow-[80px_0px_100px_80px_white]"
            v-html="pageData.description_html"
          />
          <!-- enable-disable vue/no-v-html -->
        </div>
      </div>

      <AppCamperSearchCall
        v-if="searchData && searchData.families.length >= CAMPERS_PER_ROW"
        :campers="searchData.families"
        class="container mt-20"
      >
        <template #title>
          {{ $t('pages.places.in_family') }}
        </template>
        <template #description>
          {{ $t('pages.places.family_advantages_dynamic', { placename_string: placeNameWithClosestAncestor }) }}
        </template>
        <template #cta>
          <NuxtLink
            :to="{ name: 's', query: toLocationQueryRaw(familiesQuery) }"
            class="btn btn-secondary"
          >
            {{ $t('pages.places.rent_in_family') }}
          </NuxtLink>
        </template>
      </AppCamperSearchCall>

      <AppCamperSearchCall
        v-if="searchData && searchData.couples.length >= CAMPERS_PER_ROW"
        :campers="searchData.couples"
        class="container mt-20"
      >
        <template #title>
          {{ $t('pages.places.in_couple') }}
        </template>
        <template #description>
          {{ $t('pages.places.family_couple_dynamic', { placename_string: placeNameWithClosestAncestor }) }}
        </template>
        <template #cta>
          <NuxtLink
            :to="{ name: 's', query: toLocationQueryRaw(couplesQuery) }"
            class="btn btn-secondary"
          >
            {{ $t('pages.places.rent_in_couple') }}
          </NuxtLink>
        </template>
      </AppCamperSearchCall>

      <AppCamperSearchCall
        v-if="searchData && searchData.pets.length >= CAMPERS_PER_ROW"
        :campers="searchData.pets"
        class="container mt-20"
      >
        <template #title>
          {{ $t('data.product.pets_allowed.accepted') }}
        </template>
        <template #description>
          {{ $t('pages.places.pets_advantages_dynamic', { placename_string: placeNameWithClosestAncestor }) }}
        </template>
        <template #cta>
          <NuxtLink
            :to="{ name: 's', query: toLocationQueryRaw(petsQuery) }"
            class="btn btn-secondary"
          >
            {{ $t('commons.rent_a_camper.default') }}
          </NuxtLink>
        </template>
      </AppCamperSearchCall>

      <div class="container mt-28 pb-16 space-y-14">
        <template v-if="groupedNeighbors">
          <AppLandingLinks
            v-for="grouped in groupedNeighbors"
            :key="grouped.title"
            :links="grouped.list"
          >
            <template #title>
              {{ $t('commons.rent_a_camper.default') }} — {{ grouped.title }}
            </template>

            <template #links="{ links }">
              <AppLandingLink
                v-for="neighbor in links"
                :key="neighbor.name"
                :to="{ name: 'campervan-hire-slug', params: { slug: neighbor.slug } }"
              >
                {{ neighbor.name }}
              </AppLandingLink>
              <AppLandingLink
                v-if="grouped.ancestor"
                emphasis
                :to="{ name: 'campervan-hire-slug', params: { slug: grouped.ancestor.slug } }"
              >
                {{ grouped.ancestor.name }}
              </AppLandingLink>
            </template>
          </AppLandingLinks>
        </template>

        <AppLandingLinks
          v-if="searchData"
          :links="searchData.countries"
        >
          <template #title>
            {{ $t('pages.index.camper_rental_in_europe') }}
          </template>

          <template #links="{ links }">
            <AppLandingLink
              v-for="country in links"
              :key="country.name"
              :to="{ name: 'campervan-hire-slug', params: { slug: country.slug } }"
            >
              {{ country.name }}
            </AppLandingLink>
            <AppLandingLink
              :to="{ name: 's' }"
              emphasis
            >
              {{ $t('pages.places.all_europe_in_motorhome') }}
            </AppLandingLink>
          </template>
        </AppLandingLinks>
      </div>
    </div>
  </div>
</template>
